import React, { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Search, Navigation, LocateFixed, Loader2, ArrowUpRight, AlertCircle } from 'lucide-react';
import { europeBranches as defEU, sriLankaBranches as defSL, heroImages } from '../mock/data';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useSiteSettings } from '../context/SiteSettingsContext';

// Haversine distance in km
const haversine = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
};

const geocodeCity = async (q) => {
  // Try Nominatim (OpenStreetMap) — free, no API key
  const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(q)}`;
  const res = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error('Geocoding failed');
  const data = await res.json();
  if (!data.length) return null;
  return {
    lat: parseFloat(data[0].lat),
    lng: parseFloat(data[0].lon),
    label: data[0].display_name,
  };
};

const BranchResult = ({ b, rank }) => (
  <div className="group flex flex-col md:flex-row md:items-center gap-5 p-6 bg-white border border-neutral-200 hover:border-black transition-all hover:shadow-xl">
    <div className="flex items-center gap-4">
      <div className={`w-14 h-14 flex items-center justify-center font-serif-display text-2xl font-bold shrink-0 ${rank === 0 ? 'bg-[#c8102e] text-white' : 'bg-neutral-100 text-black'}`}>
        {rank + 1}
      </div>
      <div className="md:w-56">
        <div className="text-[10px] tracking-[0.25em] uppercase text-[#c8102e] font-semibold mb-1">{b.region}</div>
        <h3 className="font-serif-display text-xl font-bold text-black">{b.name}</h3>
        {b.city && <div className="text-xs text-neutral-500 mt-0.5">{b.city}</div>}
      </div>
    </div>
    <div className="flex-1 md:pl-6 md:border-l border-neutral-200">
      {b.address && <p className="text-sm text-neutral-600 mb-2">{b.address}</p>}
      <div className="flex items-baseline gap-3">
        <span className="font-serif-display text-3xl font-bold text-black">{b.distance.toFixed(1)}</span>
        <span className="text-xs uppercase tracking-[0.2em] text-neutral-500">km away</span>
      </div>
    </div>
    <div className="flex items-center gap-3 shrink-0">
      <a href={`tel:${b.phone.replace(/\s+/g, '')}`} className="inline-flex items-center gap-2 bg-[#c8102e] hover:bg-black text-white h-11 px-5 text-[12px] tracking-[0.15em] uppercase font-semibold transition-colors">
        <Phone className="w-3.5 h-3.5" /> Call
      </a>
      <a href={`https://www.google.com/maps/dir/?api=1&destination=${b.lat},${b.lng}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-black hover:bg-black hover:text-white text-black h-11 px-5 text-[12px] tracking-[0.15em] uppercase font-semibold transition-colors">
        <Navigation className="w-3.5 h-3.5" /> Directions
      </a>
    </div>
  </div>
);

const FindBranch = () => {
  const [query, setQuery] = useState('');
  const [origin, setOrigin] = useState(null); // { lat, lng, label }
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const { getContent } = useSiteSettings();
  const europeBranches = getContent('branches_eu', defEU);
  const sriLankaBranches = getContent('branches_sl', defSL);
  const ALL_BRANCHES = useMemo(() => [
    ...europeBranches.filter((b) => b.lat && b.lng).map((b) => ({ ...b, region: b.country })),
    ...sriLankaBranches.filter((b) => b.lat && b.lng).map((b) => ({ ...b, region: 'Sri Lanka' })),
  ], [europeBranches, sriLankaBranches]);

  const search = useCallback(async (e) => {
    e && e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setErr('');
    try {
      // 1) fast local match against branch cities/names
      const local = ALL_BRANCHES.find((b) => {
        const hay = `${b.name} ${b.city || ''} ${b.country || ''}`.toLowerCase();
        return hay.includes(query.trim().toLowerCase());
      });
      if (local) {
        setOrigin({ lat: local.lat, lng: local.lng, label: `${local.city || local.name}${local.country ? ', ' + local.country : ''}` });
      } else {
        // 2) fall back to OpenStreetMap geocoding
        const g = await geocodeCity(query);
        if (!g) { setErr('We couldn\'t locate that place. Try a nearby city.'); setOrigin(null); return; }
        setOrigin(g);
      }
    } catch (e2) {
      setErr('Search failed. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [query, ALL_BRANCHES]);

  const useMyLocation = () => {
    if (!navigator.geolocation) { setErr('Geolocation is not supported by your browser.'); return; }
    setLoading(true); setErr('');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setOrigin({ lat: pos.coords.latitude, lng: pos.coords.longitude, label: 'Your current location' });
        setQuery('');
        setLoading(false);
      },
      () => { setErr('We couldn\'t access your location. Please allow the permission or search by city.'); setLoading(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const results = useMemo(() => {
    if (!origin) return [];
    return ALL_BRANCHES
      .map((b) => ({ ...b, distance: haversine(origin.lat, origin.lng, b.lat, b.lng) }))
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 5);
  }, [origin, ALL_BRANCHES]);

  return (
    <div>
      <section className="relative h-[360px] flex items-center bg-black overflow-hidden">
        <img src={heroImages.truck} alt="Find your branch" className="absolute inset-0 w-full h-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="flex items-center gap-3 mb-4"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow text-white">Nearest Branch</span></div>
          <h1 className="text-white font-serif-display text-5xl md:text-6xl font-bold leading-[1.05] max-w-2xl">Find a branch closest to you</h1>
          <p className="text-white/75 mt-4 max-w-xl">Type your city or share your location — we'll instantly show you the nearest Lanka Express outlets.</p>
        </div>
      </section>

      <section className="py-16 bg-neutral-50">
        <div className="max-w-4xl mx-auto px-6">
          <form onSubmit={search} className="bg-white p-6 md:p-8 border border-neutral-200 shadow-sm">
            <label className="block text-[10px] tracking-[0.25em] uppercase text-[#c8102e] font-semibold mb-3">Your City or Postcode</label>
            <div className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="e.g. Colombo, Milan, London, Kandy..." className="pl-11 h-14 text-base border-neutral-300 focus-visible:ring-[#c8102e]" />
              </div>
              <Button type="submit" disabled={loading} className="bg-[#c8102e] hover:bg-black text-white h-14 px-8 font-semibold tracking-[0.15em] uppercase text-[13px]">
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />} Find Branch
              </Button>
            </div>
            <div className="flex items-center gap-3 mt-4 text-sm">
              <button type="button" onClick={useMyLocation} disabled={loading} className="inline-flex items-center gap-2 text-black hover:text-[#c8102e] font-semibold transition-colors">
                <LocateFixed className="w-4 h-4" /> Use my current location
              </button>
              <span className="text-neutral-300">|</span>
              <Link to="/contact" className="text-neutral-500 hover:text-[#c8102e] transition-colors">Browse all branches</Link>
            </div>
            {err && (
              <div className="mt-4 flex items-center gap-2 text-sm text-[#c8102e]"><AlertCircle className="w-4 h-4" /> {err}</div>
            )}
          </form>

          {origin && (
            <div className="mt-10">
              <div className="flex items-center gap-3 mb-6">
                <MapPin className="w-4 h-4 text-[#c8102e]" />
                <span className="text-sm text-neutral-600">Nearest to <span className="font-semibold text-black">{origin.label}</span></span>
              </div>
              <div className="space-y-4">
                {results.map((b, i) => <BranchResult key={b.name} b={b} rank={i} />)}
              </div>
              <div className="mt-8 text-center">
                <Link to="/contact" className="inline-flex items-center gap-2 text-black hover:text-[#c8102e] font-semibold text-sm tracking-wide transition-colors">
                  Or browse all 37 branches <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          )}

          {!origin && !err && (
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
              {['Colombo', 'Milan', 'London', 'Paris', 'Kandy', 'Galle', 'Rome', 'Bucharest'].map((c) => (
                <button key={c} onClick={() => { setQuery(c); setTimeout(() => document.forms[0]?.requestSubmit(), 0); }} className="p-4 border border-neutral-200 hover:border-black hover:bg-black hover:text-white text-sm font-semibold transition-colors">{c}</button>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default FindBranch;
